import { FocusMonitor } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { Component, ElementRef, EventEmitter, Inject, Input, OnDestroy, OnInit, Optional, Output, Self, ViewChild } from '@angular/core';
import { ControlValueAccessor, UntypedFormGroup, UntypedFormBuilder, NgControl, Validators, AbstractControl, UntypedFormControl } from '@angular/forms';
import { ErrorStateMatcher, ShowOnDirtyErrorStateMatcher } from '@angular/material/core';
import { MatFormField, MatFormFieldControl, MAT_FORM_FIELD } from '@angular/material/form-field';
import { Subject } from 'rxjs';

/** Data structure for holding telephone number. */
export class PhoneInputFieldData {
    constructor(public phone: string) {}
}

@Component({
    selector: 'app-phone-input-field',
    templateUrl: './phone-input-field.component.html',
    styleUrls: ['./phone-input-field.component.scss'],
    providers: [
        { provide: MatFormFieldControl, useExisting: PhoneInputFieldComponent },
        { provide: ErrorStateMatcher, useClass: ShowOnDirtyErrorStateMatcher }
    ],
    host: {
        '[class.example-floating]': 'shouldLabelFloat',
        '[id]': 'id'
    },
    standalone: false
})
export class PhoneInputFieldComponent implements ControlValueAccessor, MatFormFieldControl<PhoneInputFieldData>, OnDestroy {
    static nextId = 0;
    @ViewChild('phone') phoneInput!: ElementRef<HTMLInputElement>;
    @Output() formBlur: EventEmitter<boolean> = new EventEmitter();

    parts: UntypedFormGroup;
    stateChanges = new Subject<void>();
    focused = false;
    touched = false;
    controlType = 'example-tel-input';
    id = `example-tel-input-${PhoneInputFieldComponent.nextId++}`;
    onChange = (_: any) => {};
    onTouched = () => {};

    get empty() {
        const {
            value: { phone }
        } = this.parts;

        return !phone;
    }

    get shouldLabelFloat() {
        return this.focused || !this.empty;
    }

    @Input('aria-describedby') userAriaDescribedBy!: string;

    @Input()
    get placeholder(): string {
        return this._placeholder;
    }
    set placeholder(value: string) {
        this._placeholder = value;
        this.stateChanges.next();
    }
    private _placeholder!: string;

    @Input()
    get required(): boolean {
        return this._required;
    }
    set required(value: BooleanInput) {
        this._required = coerceBooleanProperty(value);
        this.stateChanges.next();
    }
    private _required = false;

    @Input()
    get disabled(): boolean {
        return this._disabled;
    }
    set disabled(value: BooleanInput) {
        this._disabled = coerceBooleanProperty(value);
        this._disabled ? this.parts.disable() : this.parts.enable();
        this.stateChanges.next();
    }
    private _disabled = false;

    @Input()
    get value(): PhoneInputFieldData | null {
        const value: PhoneInputFieldData = this.parts.value;
        const val = `${value.phone}`;
        if (val?.length) {
            return { phone: this.convertToRealValue(value.phone) };
        }
        return null;
    }
    set value(tel: PhoneInputFieldData | null) {;
        this.parts.setValue({ phone: tel?.phone ? this.convertToMaskValue(tel.phone, false) : '' });
        this.stateChanges.next();
    }

    get errorState(): boolean {
        return this._errorStateMatcher.isErrorState(this.ngControl.control as UntypedFormControl, null);
    }

    constructor(
        formBuilder: UntypedFormBuilder,
        private readonly _focusMonitor: FocusMonitor,
        private readonly _errorStateMatcher: ErrorStateMatcher,
        private readonly _elementRef: ElementRef<HTMLElement>,
        @Optional() @Inject(MAT_FORM_FIELD) public _formField: MatFormField,
        @Optional() @Self() public ngControl: NgControl
    ) {
        this.parts = formBuilder.group({
            phone: [null, [Validators.required, Validators.minLength(10), Validators.maxLength(10)]]
        });

        if (this.ngControl != null) {
            this.ngControl.valueAccessor = this;
        }
    }

    ngOnDestroy() {
        this.stateChanges.complete();
        this._focusMonitor.stopMonitoring(this._elementRef);
    }

    onFocusIn(event: FocusEvent) {
        if (!this.focused) {
            this.focused = true;
            this.stateChanges.next();
        }
    }

    onFocusOut(event: FocusEvent) {
        if (!this._elementRef.nativeElement.contains(event.relatedTarget as Element)) {
            this.touched = true;
            this.focused = false;
            this.onTouched();
            this.stateChanges.next();
        }
        this.formBlur.emit(false);
    }

    setDescribedByIds(ids: string[]) {
        const controlElement = this._elementRef.nativeElement.querySelector('.example-tel-input-container')!;
        controlElement.setAttribute('aria-describedby', ids.join(' '));
    }

    onContainerClick(event: MouseEvent) {
        if (this.phoneInput?.nativeElement && !this._disabled) {
            this.phoneInput.nativeElement.focus();
        }
    }

    writeValue(value: PhoneInputFieldData): void {
        this.value = value;
        if (this.phoneInput?.nativeElement) {
          if (value === undefined || value == null) {
            this.phoneInput.nativeElement.value = '';
          } else {
            const maskValue = this.convertToMaskValue(value.phone, false);
            this.phoneInput.nativeElement.value = maskValue;
          }
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    onInputChange(event: any) {
        const inputElement = event.target as HTMLInputElement;
        let cursorPositionStart = inputElement.selectionStart || 0;
        let cursorPositionEnd = inputElement.selectionEnd || 0;
        
        setTimeout(() => {
          const maskValue = this.convertToMaskValue(inputElement.value, event.inputType === 'deleteContentBackward');
          this.value = new PhoneInputFieldData(maskValue);
          this.onChange(this.value);
        
          let splittedHalfNumbers = this.convertToRealValue(inputElement.value.slice(cursorPositionEnd));

          if ((/^\d{2,}/).test(splittedHalfNumbers)) {
              inputElement.setSelectionRange(cursorPositionStart, cursorPositionEnd);
          }
        }, 0);
      }
      
    private convertToMaskValue(value: string, backspace: boolean): string {
      let newVal = value;
      if (newVal && newVal.length > 0) {
        if (backspace && value.length <= 12) {
          newVal = value.substring(0, value.length - 1);
        }
        newVal = this.convertToRealValue(newVal);
        if (newVal.length === 0) {
          newVal = '';
        } else if (newVal.length <= 3) {
          newVal = newVal.replace(/^(\d{0,3})/, '($1)');
        } else if (newVal.length <= 6) {
          newVal = newVal.replace(/^(\d{0,3})(\d{0,3})/, '($1) $2');
        } else if (newVal.length <= 10) {
          newVal = newVal.replace(/^(\d{0,3})(\d{0,3})(\d{0,4})/, '($1) $2-$3');
        } else {
          newVal = newVal.substring(0, 10);
          newVal = newVal.replace(/^(\d{0,3})(\d{0,3})(\d{0,4})/, '($1) $2-$3');
        }
      }
      return newVal;
    }
  
    private convertToRealValue(value: string): string {
      return value.replace(/\D/g, '');
    }

}